import React from 'react';
import {
    GitGraph,
    ShieldCheck,
    PackageSearch,
    Import,
    Puzzle,
} from 'lucide-react';
import { Button } from 'primereact/button';

const DefaultTab = ({ onPromptClick, chatId, selectedCollection, showPrompts, handleImport }) => {
    const promptCategories = [
        {
            title: 'API Discovery',
            icon: <PackageSearch size={18} className="text-[var(--primary-color)]" />,
            prompts: [
                {
                    text: 'Discover my APIs',
                    onClick: (e) => onPromptClick('Help me discover my APIs from the code. Build a swagger file for all my APIs')
                },
                ...(selectedCollection.length === 0 ? [
                    {
                        text: 'Import Postman Collection',
                        onClick: (e) => handleImport('postman'),
                        icon: <Import size={16} className="mr-2" />
                    },
                    {
                        text: 'Import Swagger Collection',
                        onClick: (e) => handleImport('swagger'),
                        icon: <Import size={16} className="mr-2" />
                    }
                ] : [])
            ]
        },
        {
            title: 'Unit-Test Suites',
            icon: <Puzzle size={18} className="text-[var(--primary-color)]" />,
            prompts: [
                {
                    text: 'Full CRUD Unit Suite',
                    onClick: (e) => onPromptClick('Generate unit tests for every CRUD endpoint in my current collection—positive, negative, and boundary cases for each.')
                },
                {
                    text: 'Parameter Validation Blitz',
                    onClick: (e) => onPromptClick('Create unit tests that systematically omit or corrupt every required query, path, and body parameter to verify 4xx error handling.')
                },
                {
                    text: 'Auth Header Hardening',
                    onClick: (e) => onPromptClick('Produce unit tests that hit all endpoints with missing, expired, and malformed auth tokens to confirm proper 401/403 responses.')
                },
            ]
        },
        {
            title: 'Functional-Flow Suites',
            icon: <ShieldCheck size={18} className="text-[var(--primary-color)]" />,
            prompts: [
                {
                    text: 'Signup → Login → CRUD Regression',
                    onClick: (e) => onPromptClick('Build an end-to-end regression suite that walks a new user through signup, login, full CRUD on a resource and cleanup asserting data persistence and cleanup.')
                },
                {
                    text: 'Payment Module Deep-Dive',
                    onClick: (e) => onPromptClick('Generate functional tests for the payment workflow: authorize charge, capture, refund, and concurrent transactions. Cover success and failure flows.')
                },
                {
                    text: 'Order-to-Shipment Workflow',
                    onClick: (e) => onPromptClick('Write functional tests that create an order, generate an invoice, trigger shipment, and track status updates—validating state transitions at each step.')
                },
            ]
        },
        {
            title: 'Security Tests',
            icon: <GitGraph size={18} className="text-[var(--primary-color)]" />,
            prompts: [
                {
                    text: 'OWASP Top-10 Audit',
                    onClick: (e) => onPromptClick('Create a security suite that probes the collection for all OWASP API Top-10 risks. Provide at least one exploit attempt and expected defensive response per item.')
                },
                {
                    text: 'Pen-Test Sweep',
                    onClick: (e) => onPromptClick('Generate penetration-test scenarios that mimic real-world attacker behavior—SQL/NoSQL injection, auth bypass, rate-limit abuse, directory traversal, and file-upload exploits. Chain auth steps as needed and flag critical findings.')
                },
                {
                    text: 'Qodex 50+ Vuln Scan',
                    onClick: (e) => onPromptClick('Run the full Qodex security battery (50+ vulnerability patterns). Include tests for business-logic abuse, SSRF, mass-assignment, JWT tampering, and other advanced risks. Summarize any vulnerabilities with remediation tips.')
                },
            ]
        }
    ];

    return (
        <div className="w-full flex flex-col h-full bg-[var(--primary-bg)]">
            <div className="w-full flex-1 overflow-y-auto px-4 py-6 chatscreen-custom-scrollbar">
                <div className="w-full space-y-6">
                    <div className="text-center mb-3">
                        <h2 className="font-normal text-[var(--text-primary)] fs24 mb-1">
                            Welcome to Qodex AI Assistant!
                        </h2>
                        <p className="text-[var(--text-secondary)] fs14">
                            Your intelligent companion for API testing and automation
                        </p>
                    </div>
                    {/* Prompt Categories */}
                    {showPrompts && (
                        <div className="grid grid-cols-1 gap-4">
                            {promptCategories.map((category, idx) => (
                                <div
                                    key={idx}
                                    className="bg-[var(--secondary-bg)] text-[var(--text-primary)] rounded-md py-2.5 px-4 shadow-md border !border-[var(--borderColor)]"
                                >
                                    <div className="flex items-center mb-3">
                                        <span className='mr-2'>{category.icon}</span>
                                        <span className="fs16 font-semibold text-[var(--text-primary)]">{category.title}</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        {category.prompts.map((prompt, promptIdx) => (
                                            <Button
                                                key={promptIdx}
                                                onClick={prompt.onClick}
                                                className={`text-left px-3 py-2 !rounded-md flex items-center group shadow-none relative bg-[var(--primary-bg)] hover:bg-[var(--primary-bg)]/20 border !border-[var(--borderColor)] hover:border-[var(--primary-color)]/50 text-[var(--text-secondary)] hover:text-[var(--text-primary)] ${prompt.text.includes('Import')
                                                    ? 'before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-[var(--primary-color)] before:rounded-l'
                                                    : ''
                                                    } transition-all duration-200 ease-in-out`}
                                                aria-label={`Use prompt: ${prompt.text}`}
                                            >
                                                {prompt.icon}
                                                <span className="flex-1 fs13">{prompt.text}</span>
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DefaultTab;
